import { useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Bot,
  Check,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Loader2,
  Settings2,
  ShoppingCart,
  Sparkles,
  Wrench,
} from 'lucide-react';
import { useStripe } from '../contexts/StripeContext';
import {
  getRoboticsProduct,
  type RoboticsProductId,
  type RoboticsVariantId,
} from '../lib/roboticsProducts';
import AeoPageHead from '../components/AeoPageHead';
import { getRoboticsProductPageFaqs } from '../lib/aeo/faqs';
import { buildRoboticsProductJsonLd } from '../lib/aeo/jsonLd';

export default function RoboticsProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const product = productId ? getRoboticsProduct(productId) : undefined;
  const [searchParams] = useSearchParams();
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';
  const { openCheckout } = useStripe();
  const [imageIndex, setImageIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState<RoboticsVariantId>('pro');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  if (!product) {
    return <Navigate to="/robotics" replace />;
  }

  const paginateImage = (direction: number) => {
    setImageIndex((prev) => {
      const next = prev + direction;
      if (next < 0) return product.images.length - 1;
      if (next >= product.images.length) return 0;
      return next;
    });
  };

  const handleCheckout = async () => {
    try {
      setCheckoutLoading(true);
      setCheckoutError(null);
      await openCheckout(product.id as RoboticsProductId, {
        variant: product.variants ? selectedVariant : undefined,
      });
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : 'Checkout failed');
      setCheckoutLoading(false);
    }
  };

  return (
    <div
      id="robotics"
      data-avatar-target="robotics"
      className="relative min-h-screen bg-black text-white"
    >
      <Helmet>
        <title>{product.name} — SELF Labs Robotics</title>
        <meta name="description" content={product.shortDescription} />
        <meta property="og:title" content={`${product.name} — SELF Labs Robotics`} />
        <meta property="og:description" content={product.tagline} />
        <meta property="og:image" content={product.images[0].src} />
      </Helmet>
      <AeoPageHead
        path={`/robotics/${product.id}`}
        faqs={getRoboticsProductPageFaqs(product)}
        extraJsonLd={[buildRoboticsProductJsonLd(product)]}
      />

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-indigo-600/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-cyan-600/10 blur-3xl" />
      </div>

      <div className="relative px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <Link
            to="/robotics"
            className="group inline-flex items-center text-sm text-white/50 transition-colors hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Back to Robotics
          </Link>

          {checkoutCancelled && (
            <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              Checkout was cancelled. You can try again when you are ready.
            </div>
          )}

          <div className="mt-10 grid gap-12 lg:grid-cols-2 lg:items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                <Bot className="h-3.5 w-3.5" />
                SELF Labs Robotics
              </span>

              <div className="relative mt-6 overflow-hidden rounded-3xl border border-white/12 bg-white">
                <div className="flex min-h-[380px] items-center justify-center px-4 py-6 sm:min-h-[480px] sm:px-6 sm:py-8">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={product.images[imageIndex].src}
                      src={product.images[imageIndex].src}
                      alt={product.images[imageIndex].alt}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="mx-auto h-auto max-h-[min(70vh,520px)] w-auto max-w-full object-contain object-center"
                    />
                  </AnimatePresence>
                </div>

                {product.images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => paginateImage(-1)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-black/60 p-2 text-white/80 backdrop-blur-sm transition hover:bg-black/80"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => paginateImage(1)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-black/60 p-2 text-white/80 backdrop-blur-sm transition hover:bg-black/80"
                      aria-label="Next image"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}
              </div>

              {product.images.length > 1 && (
                <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                  {product.images.map((image, index) => (
                    <button
                      key={image.src}
                      type="button"
                      onClick={() => setImageIndex(index)}
                      className={`flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white p-1 transition ${
                        index === imageIndex
                          ? 'border-indigo-400/60 ring-2 ring-indigo-400/30'
                          : 'border-white/10 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image.src}
                        alt=""
                        className="h-full w-full object-contain object-center"
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{product.name}</h1>
              <p className="mt-4 text-lg leading-relaxed text-indigo-200/80">{product.tagline}</p>
              <p className="mt-4 text-base leading-relaxed text-white/60">{product.shortDescription}</p>

              {product.variants && (
                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Choose configuration
                  </p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {product.variants.map((variant) => (
                      <button
                        key={variant.id}
                        type="button"
                        onClick={() => setSelectedVariant(variant.id)}
                        className={`rounded-xl border p-4 text-left transition ${
                          selectedVariant === variant.id
                            ? 'border-indigo-400/50 bg-indigo-500/10'
                            : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                        }`}
                      >
                        <p className="font-semibold">{variant.name}</p>
                        <p className="mt-1 text-sm text-white/55">{variant.summary}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 rounded-2xl border border-white/12 bg-white/[0.03] p-6">
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                      Platform price
                    </p>
                    <p className="mt-1 text-3xl font-bold">{product.priceLabel}</p>
                    <p className="mt-1 text-sm text-white/45">
                      USD · one-time purchase
                      {product.variants && (
                        <>
                          {' '}
                          ·{' '}
                          {product.variants.find((v) => v.id === selectedVariant)?.name} configuration
                        </>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 px-6 py-3 text-sm font-semibold text-white transition hover:from-indigo-400 hover:to-cyan-400 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Redirecting…
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4" />
                        Buy now
                      </>
                    )}
                  </button>
                </div>

                {checkoutError && <p className="mt-4 text-sm text-red-300">{checkoutError}</p>}

                <p className="mt-4 text-sm text-white/45">
                  Secure checkout powered by Stripe. A member of our team will follow up after
                  purchase to confirm shipping, configuration, customization options, and deployment
                  planning.
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/build"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white/80 transition hover:bg-white/10"
                >
                  <Wrench className="h-4 w-4" />
                  Request custom software package
                </Link>
                <Link
                  to="/role-model-ai"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white/80 transition hover:bg-white/10"
                >
                  <Sparkles className="h-4 w-4" />
                  Pair with AI agents
                </Link>
              </div>
            </motion.div>
          </div>

          <section className="mt-20 border-t border-white/10 pt-16">
            <div className="grid gap-10 lg:grid-cols-2">
              <div>
                <h2 className="text-2xl font-semibold">About the platform</h2>
                <div className="mt-4 space-y-4 whitespace-pre-line text-sm leading-relaxed text-white/65">
                  {product.description}
                </div>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold">
                    <Cpu className="h-5 w-5 text-indigo-400" />
                    Best for
                  </h3>
                  <ul className="mt-4 space-y-2">
                    {product.bestFor.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-white/65">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold">Key highlights</h3>
                  <ul className="mt-4 space-y-2">
                    {product.highlights.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-white/65">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {product.variants && (
            <section className="mt-20 border-t border-white/10 pt-16">
              <h2 className="text-2xl font-semibold">What&apos;s the difference?</h2>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {product.variants.map((variant) => (
                  <div
                    key={variant.id}
                    className="rounded-2xl border border-white/12 bg-white/[0.03] p-6"
                  >
                    <h3 className="text-lg font-semibold">{variant.name} version</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/65">{variant.summary}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {product.simplifiedSpecs && (
            <section className="mt-20 border-t border-white/10 pt-16">
              <div className="mb-8 flex items-center gap-3">
                <Settings2 className="h-5 w-5 text-indigo-400" />
                <h2 className="text-2xl font-semibold">At a glance</h2>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {product.simplifiedSpecs.map((spec) => (
                  <div
                    key={spec.parameter}
                    className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
                  >
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                      {spec.parameter}
                    </p>
                    <p className="mt-1 text-sm text-white/75">{spec.specification}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {product.specs && (
            <section className="mt-20 border-t border-white/10 pt-16">
              <div className="mb-8 flex items-center gap-3">
                <Settings2 className="h-5 w-5 text-indigo-400" />
                <h2 className="text-2xl font-semibold">Technical specifications</h2>
              </div>
              <div className="overflow-hidden rounded-2xl border border-white/12 bg-white/[0.03]">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/[0.04]">
                        <th className="px-5 py-4 font-semibold text-white/80">Parameter</th>
                        <th className="px-5 py-4 font-semibold text-white/80">Specification</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.specs.map((spec, index) => (
                        <tr
                          key={spec.parameter}
                          className={
                            index < product.specs!.length - 1 ? 'border-b border-white/8' : ''
                          }
                        >
                          <td className="px-5 py-4 align-top font-medium text-white/75">
                            {spec.parameter}
                          </td>
                          <td className="px-5 py-4 align-top text-white/60">{spec.specification}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {product.comparisonSpecs && (
            <section className="mt-20 border-t border-white/10 pt-16">
              <div className="mb-8 flex items-center gap-3">
                <Settings2 className="h-5 w-5 text-indigo-400" />
                <h2 className="text-2xl font-semibold">Technical specifications</h2>
              </div>
              <div className="overflow-hidden rounded-2xl border border-white/12 bg-white/[0.03]">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/[0.04]">
                        <th className="px-5 py-4 font-semibold text-white/80">Specification</th>
                        <th className="px-5 py-4 font-semibold text-white/80">SELF Dog E1 Pro</th>
                        <th className="px-5 py-4 font-semibold text-white/80">SELF Dog E1 Edu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.comparisonSpecs.map((spec, index) => (
                        <tr
                          key={spec.parameter}
                          className={
                            index < product.comparisonSpecs!.length - 1
                              ? 'border-b border-white/8'
                              : ''
                          }
                        >
                          <td className="px-5 py-4 align-top font-medium text-white/75">
                            {spec.parameter}
                          </td>
                          <td className="px-5 py-4 align-top text-white/60">{spec.pro}</td>
                          <td className="px-5 py-4 align-top text-white/60">{spec.edu}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
